import type { OutboxOp } from "../db/outbox.ts";

/**
 * What the sync engine needs from a server. Kept behind an interface so the
 * engine can be tested against an in-memory server that fails, duplicates
 * and reorders on purpose — which is the only way to know the queue really
 * is idempotent.
 */
/**
 * A patch that reached the server and lost.
 *
 * `session_field_patch()` compares it against the newest recorded change to
 * that field and keeps the other one, answering false. That is neither a
 * failure nor a success: the write was delivered and answered, so retrying it
 * can never change the outcome — but the value the user typed is not the one
 * that stands, and they were never told. The queue drops it; the bar says so
 * once, and the losing value stays in the server's audit log.
 */
export class Superseded extends Error {
  readonly field: string;
  constructor(field: string) {
    super(`palvelimella on uudempi arvo kentälle ${field}`);
    this.name = "Superseded";
    this.field = field;
  }
}

export interface Transport {
  /**
   * The saunas this account belongs to. Asked without an id on purpose:
   * a device that has never synced does not know the id of a sauna someone
   * else created, so "does sauna X exist" can only ever answer no — and the
   * device then creates a second sauna of its own. Row-level security makes
   * an unfiltered select return exactly this member's saunas.
   */
  mySaunas(): Promise<Record<string, unknown>[]>;

  /** Apply one queued operation. Must be safe to call twice with the same op. */
  apply(op: OutboxOp): Promise<void>;
  /** Everything in this sauna changed at or after `since` (null = everything). */
  pull(sauna_id: string, since: string | null): Promise<PullResult>;
}

export interface PullResult {
  server_time: string;
  rows: Partial<Record<string, Record<string, unknown>[]>>;
}
